import { useEffect, useRef, useState } from 'react';

const isActive = () => document.visibilityState === 'visible' && document.hasFocus() && navigator.onLine;

/**
 * Polling clock for the dashboard: `tick` increments every `intervalMs` while
 * the tab is visible, focused and online, and at once on regaining any of
 * those if a tick was missed meanwhile. Consumers refetch when `tick` changes.
 * `active` says whether the clock is running, `online` mirrors navigator.onLine.
 */
const useLiveRefresh = (intervalMs) => {
  const [tick, setTick] = useState(0);
  const [active, setActive] = useState(isActive);
  const [online, setOnline] = useState(() => navigator.onLine);
  const lastTick = useRef(Date.now());

  useEffect(() => {
    let timer = null;
    const fire = () => { lastTick.current = Date.now(); setTick((t) => t + 1); };
    const sync = () => {
      clearInterval(timer);
      timer = null;
      const now = isActive();
      setActive(now);
      setOnline(navigator.onLine);
      if (!now) return;
      if (Date.now() - lastTick.current >= intervalMs) fire();
      timer = setInterval(fire, intervalMs);
    };
    const events = [[window, 'focus'], [window, 'blur'], [window, 'online'], [window, 'offline'], [document, 'visibilitychange']];
    events.forEach(([target, name]) => target.addEventListener(name, sync));
    sync();
    return () => {
      clearInterval(timer);
      events.forEach(([target, name]) => target.removeEventListener(name, sync));
    };
  }, [intervalMs]);

  return { tick, active, online };
};

export default useLiveRefresh;
